/**
 * BreakdownPanel
 *
 * The reviewable half of the breakdown: the summary, one section per proposed
 * group, and the commit action. Rejection is carried entirely by each
 * `ProposedStoryCard` — this panel just tracks which ids are rejected and
 * shows a running count of what commit will actually do.
 *
 * Since FLOW-694 a proposal is a list of groups rather than one epic, so the
 * per-group framing lives in `BreakdownGroupSection` and this panel repeats it.
 * A one-epic breakdown is the single-group case, unchanged on screen.
 */

import { Component, For, Show } from "solid-js";
import type { InitiativeBreakdown } from "../../api/types";
import { Button } from "../ui/Button";
import { BreakdownGroupSection } from "./BreakdownGroupSection";
import { formatEstimateTotal } from "../../utils/estimate";

export interface BreakdownPanelProps {
  breakdown: InitiativeBreakdown;
  rejectedStoryIds: Set<string>;
  onToggleStory: (id: string) => void;
  approvedCount: number;
  totalEstimate: number;
  busy: boolean;
  onCommit: () => void;
  /** Ask to throw the whole proposal away — the caller confirms first */
  onDiscard: () => void;
}

export const BreakdownPanel: Component<BreakdownPanelProps> = (props) => {
  const groups = () => props.breakdown.payload.groups;
  const storyCount = () =>
    groups().reduce((total, group) => total + group.stories.length, 0);
  const unit = () => props.breakdown.grounding.estimate_unit;
  const total = () => formatEstimateTotal(props.totalEstimate, unit());

  return (
    <div class="flex flex-col gap-6 p-6">
      <p class="text-sm text-ink-2 whitespace-pre-wrap">
        {props.breakdown.payload.summary}
      </p>

      <For each={groups()}>
        {(group, index) => (
          <BreakdownGroupSection
            group={group}
            index={index()}
            rejectedStoryIds={props.rejectedStoryIds}
            onToggleStory={props.onToggleStory}
          />
        )}
      </For>

      <div class="flex flex-wrap items-center justify-between gap-4 border-t border-line pt-4">
        <p class="text-sm text-ink-3">
          {props.approvedCount} of {storyCount()} stories
          <Show when={total()}>{(label) => <> · {label()}</>}</Show>
        </p>
        <div class="flex items-center gap-3">
          {/* Not tied to `approvedCount`: rejecting every story is exactly
              when someone wants out, and that is when commit is disabled. */}
          <button
            type="button"
            class="text-sm font-medium text-signal-red hover:text-ink disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={props.busy}
            onClick={props.onDiscard}
          >
            Discard breakdown
          </button>
          <Button
            variant="primary"
            onClick={props.onCommit}
            disabled={props.approvedCount === 0 || props.busy}
            loading={props.busy}
          >
            Commit approved stories
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BreakdownPanel;
